import React, { useState } from 'react';
import { Copy, Check, RotateCcw } from 'lucide-react';
import type {
  Archetype,
  ArchetypeMatch,
  DimensionId,
} from '../../types/investmentPhilosophy';
import { ARCHETYPES, DIMENSION_ORDER } from '../../config/investmentArchetypes';
import { IPTopMatchCard } from './IPTopMatchCard';
import { IPDimensionBar } from './IPDimensionBar';
import { IPSecondaryMatches } from './IPSecondaryMatches';

interface IPResultsScreenProps {
  matches: ArchetypeMatch[];
  profile: Record<DimensionId, number>;
  onRestart: () => void;
}

export const IPResultsScreen: React.FC<IPResultsScreenProps> = ({
  matches,
  profile,
  onRestart,
}) => {
  const [copied, setCopied] = useState(false);
  const archetypeMap = React.useMemo(() => {
    const map: Record<string, Archetype> = {};
    ARCHETYPES.forEach((a) => {
      map[a.id] = a;
    });
    return map;
  }, []);

  const top = matches[0];
  const topArchetype = archetypeMap[top.archetypeId];
  const secondary = matches.slice(1);

  const handleShare = async () => {
    const text = `My investing archetype: ${top.percentage}% ${topArchetype.name}, ${topArchetype.descriptor}. Find yours at wallstreetle.com/investment-philosophy`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div className="w-full pb-12">
      <IPTopMatchCard archetype={topArchetype} percentage={top.percentage} />

      <div className="mt-8">
        <h3
          className="font-serif text-lg mb-3"
          style={{ color: 'hsl(var(--foreground))' }}
        >
          Your dimensions
        </h3>
        <div className="space-y-4">
          {DIMENSION_ORDER.map((dim) => (
            <IPDimensionBar key={dim} dimension={dim} value={profile[dim]} />
          ))}
        </div>
      </div>

      <div className="mt-8">
        <h3
          className="font-serif text-lg mb-3"
          style={{ color: 'hsl(var(--foreground))' }}
        >
          Other influences
        </h3>
        <IPSecondaryMatches matches={secondary} archetypeMap={archetypeMap} />
      </div>

      <div className="mt-8 flex flex-col items-center gap-3">
        <button
          onClick={handleShare}
          className="flex items-center gap-2 px-10 py-3 rounded-full font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.97]"
          style={{
            backgroundColor: copied ? 'var(--clue-green-mid)' : 'var(--clue-green-accent)',
            color: '#fff',
          }}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Share your result'}
        </button>
        <button
          onClick={onRestart}
          className="flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm transition-all hover:opacity-80 active:scale-[0.97]"
          style={{
            backgroundColor: 'var(--clue-bg-card)',
            color: 'hsl(var(--foreground))',
            border: '1px solid var(--clue-border-light)',
          }}
        >
          <RotateCcw className="w-4 h-4" />
          Retake quiz
        </button>
      </div>
    </div>
  );
};
